import React from "react";
import { useTranslation } from "react-i18next";
import { TbChevronLeft, TbChevronRight, TbChevronsLeft, TbChevronsRight } from "react-icons/tb";

interface BmsPaginationProps {
  total: number;
  skip: number;
  limit: number;
  onPageChange: (newSkip: number) => void;
  onLimitChange?: (newLimit: number) => void;
  limitOptions?: number[];
}

const BmsPagination: React.FC<BmsPaginationProps> = ({
  total,
  skip,
  limit,
  onPageChange,
  onLimitChange,
  limitOptions = [10, 25, 50],
}) => {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(skip / limit) + 1;

  if (total <= 0) return null;

  const goTo = (page: number) => {
    const clamped = Math.max(1, Math.min(page, totalPages));
    onPageChange((clamped - 1) * limit);
  };

  const rangeStart = skip + 1;
  const rangeEnd = Math.min(skip + limit, total);

  return (
    <div className="bms-pagination">
      <div className="bms-pagination-info">
        <span>
          {rangeStart}–{rangeEnd} / {total} {t("records")}
        </span>
        {onLimitChange && (
          <select
            className="bms-pagination-select"
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
          >
            {limitOptions.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        )}
      </div>
      <div className="bms-pagination-controls">
        <button
          className="bms-pagination-btn"
          onClick={() => goTo(1)}
          disabled={currentPage <= 1}
          title={t("first_page")}
        >
          <TbChevronsLeft />
        </button>
        <button
          className="bms-pagination-btn"
          onClick={() => goTo(currentPage - 1)}
          disabled={currentPage <= 1}
          title={t("previous_page")}
        >
          <TbChevronLeft />
        </button>

        {buildPageNumbers(currentPage, totalPages).map((p, i) =>
          p === "..." ? (
            <span key={`dots-${i}`} className="bms-pagination-dots">…</span>
          ) : (
            <button
              key={p}
              className={`bms-pagination-btn ${p === currentPage ? "active" : ""}`}
              onClick={() => goTo(p as number)}
            >
              {p}
            </button>
          )
        )}

        <button
          className="bms-pagination-btn"
          onClick={() => goTo(currentPage + 1)}
          disabled={currentPage >= totalPages}
          title={t("next_page")}
        >
          <TbChevronRight />
        </button>
        <button
          className="bms-pagination-btn"
          onClick={() => goTo(totalPages)}
          disabled={currentPage >= totalPages}
          title={t("last_page")}
        >
          <TbChevronsRight />
        </button>
      </div>
    </div>
  );
};

function buildPageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "...")[] = [];
  pages.push(1);
  if (current > 3) pages.push("...");
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push("...");
  pages.push(total);
  return pages;
}

export default BmsPagination;
